/* eslint-disable */
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { connect } from "react-redux";
import ApiEndpoint from "../../config/ApiEndpoint";
import ApiServices from "../../config/ApiServices";
import Constants from "../../config/Constants";
import { toast } from "react-toastify";
import { Types } from "../../constants/actionTypes";
import { PageHeader, Card } from "../shared";

const AdminAccount = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const profile = props?.profile || props?.props?.profile || {};
  const token = profile?.token || props?.props?.profile?.token;
  const userType = profile?.user_type || profile?.data?.user_type || props?.props?.profile?.user_type;
  const userRole = profile?.role || profile?.data?.role || props?.props?.profile?.role;

  const [organisationName, setOrganisationName] = useState(profile?.organisation_name || profile?.display_name || "");
  const [profileImageUrl, setProfileImageUrl] = useState(profile?.profile_image || "");
  const [idItemProfile, setIdItemProfile] = useState(undefined); // undefined = no change, null = clear, number = set
  const [saving, setSaving] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);

  const fetchAdminProfile = async () => {
    if (!token || userType !== "admin") return;
    setLoadingProfile(true);
    const headers = { "Content-Type": "application/json", "x-access-token": token };
    try {
      const data = await ApiServices.PostApiCall(
        ApiEndpoint.ADMIN_PROFILE_GET,
        JSON.stringify({}),
        headers
      );
      if (data?.status && data?.data) {
        const d = data.data;
        const name = d.organisation_name ?? d.organisationDetails?.name ?? "";
        const image = d.profile_image ?? d.logo_url ?? "";
        setOrganisationName(name);
        setProfileImageUrl(image);
        const updated = { ...profile, organisation_name: name, profile_image: image };
        if (props?.save_user_data) props.save_user_data({ user: updated });
      }
    } catch (e) {
      console.error("Failed to fetch admin profile", e);
    } finally {
      setLoadingProfile(false);
    }
  };

  useEffect(() => {
    if (userType === "admin" && token) fetchAdminProfile();
  }, [userType, token]);

  useEffect(() => {
    setOrganisationName(profile?.organisation_name || profile?.display_name || "");
    setProfileImageUrl(profile?.profile_image || "");
  }, [profile?.organisation_name, profile?.display_name, profile?.profile_image]);

  const uploadImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !token) return null;
    const formData = new FormData();
    formData.append("file", file, file.name);
    formData.append("type", "image");
    try {
      const response = await fetch(`${Constants.BASE_API_URL}user/upload`, {
        method: "POST",
        headers: { "x-access-token": token },
        body: formData,
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Upload error", error);
      return null;
    }
  };

  const handleProfileImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }
    if (!/^image\/(jpeg|png|jpg)$/.test(file.type)) {
      toast.error("Only JPG and PNG files are allowed.");
      return;
    }
    const result = await uploadImage(event);
    if (result?.status && result?.data?.id) {
      setIdItemProfile(result.data.id);
      if (result.data.url) setProfileImageUrl(result.data.url);
      toast.success("Image uploaded");
    } else {
      toast.error("Failed to upload image.");
    }
  };

  const handleClearProfileImage = () => {
    setIdItemProfile(null);
    setProfileImageUrl("");
  };

  const handleSave = async () => {
    const orgTrim = organisationName?.trim();
    const hasOrgName = orgTrim !== (profile?.organisation_name || profile?.display_name || "");
    const hasImageChange = idItemProfile !== undefined;

    if (!hasOrgName && !hasImageChange) {
      toast.error("Change at least one field (club name or profile image) to save.");
      return;
    }

    const body = {};
    if (hasOrgName) body.organisation_name = orgTrim;
    if (idItemProfile !== undefined) body.id_item_profile = idItemProfile;

    if (Object.keys(body).length === 0) {
      toast.error("No changes to save.");
      return;
    }

    setSaving(true);
    const headers = {
      "Content-Type": "application/json",
      "x-access-token": token,
    };
    try {
      const data = await ApiServices.PostApiCall(
        ApiEndpoint.ADMIN_PROFILE_UPDATE,
        JSON.stringify(body),
        headers
      );
      setSaving(false);
      if (data?.status && data?.data) {
        toast.success(data.message || "Profile updated successfully");
        const d = data.data;
        const newName = d.organisation_name ?? organisationName?.trim();
        const newImage = d.profile_image ?? profileImageUrl;
        setOrganisationName(newName);
        setProfileImageUrl(newImage);
        setIdItemProfile(undefined);
        const updated = { ...profile, organisation_name: newName, profile_image: newImage };
        if (props?.save_user_data) props.save_user_data({ user: updated });
      } else {
        toast.error(data?.message || "Failed to update profile");
      }
    } catch (error) {
      setSaving(false);
      toast.error("Failed to update profile.");
    }
  };

  if (userType !== "admin") {
    return (
      <Box>
        <PageHeader title="Account Settings" subtitle="Access denied." />
        <Card>
          <Typography sx={{ color: isDark ? "#cbd5e1" : "#718096" }}>
            This page is for platform admin accounts only.
          </Typography>
        </Card>
      </Box>
    );
  }

  const displayName = organisationName?.trim() || "Admin";
  const initials = organisationName?.trim()?.slice(0, 2).toUpperCase() || "A";

  return (
    <Box>
      <PageHeader
        title="Account Settings"
        subtitle="Update your club name and profile photo"
        breadcrumbs={[{ label: "Admin" }, { label: "Account Settings" }]}
      />
      <Card>
        <Box sx={{ maxWidth: 480 }}>
          {loadingProfile && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">Loading profile…</Typography>
            </Box>
          )}
          <Typography
            sx={{
              fontSize: "1.1rem",
              fontWeight: 600,
              color: isDark ? "#ffffff" : "#1a202c",
              mb: 2,
            }}
          >
            Profile
          </Typography>

          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 3, flexWrap: "wrap" }}>
            <Avatar
              src={profileImageUrl}
              sx={{
                width: 96,
                height: 96,
                bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                color: isDark ? "#818cf8" : "#1976d2",
                fontSize: "2rem",
                fontWeight: 600,
              }}
            >
              {initials}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <TextField
                fullWidth
                label="Club name"
                placeholder="e.g. Platform Admin"
                value={organisationName}
                onChange={(e) => setOrganisationName(e.target.value)}
                margin="normal"
                size="small"
                helperText="Shown in the app and when you send alerts"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#f7fafc",
                  },
                }}
              />
              <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                  disabled={saving}
                  sx={{ textTransform: "none" }}
                >
                  Upload photo
                  <input
                    type="file"
                    hidden
                    accept="image/jpeg,image/png,image/jpg"
                    onChange={handleProfileImageChange}
                  />
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleClearProfileImage}
                  disabled={saving || (!profileImageUrl && typeof idItemProfile !== "number")}
                  sx={{
                    textTransform: "none",
                    color: isDark ? "#f87171" : "#dc2626",
                    borderColor: isDark ? "#f87171" : "#dc2626",
                  }}
                >
                  Clear photo
                </Button>
              </Box>
            </Box>
          </Box>

          <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || loadingProfile}
              sx={{ textTransform: "none" }}
            >
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  profile: state.user.profile,
});

const mapDispatchToProps = (dispatch) => ({
  save_user_data: (data) => dispatch({ type: Types.LOGIN, payload: data }),
});

export default connect(mapStateToProps, mapDispatchToProps)(AdminAccount);
